import { useContext, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Label,
  Select,
  TextInput,
  Textarea,
} from "flowbite-react";
import { CreateCampaign } from "../../services/creditBonus/creditBonusActions";
import { ResponseContext } from "../../context/responseProvider";
import ReactSelect from "react-select";
import { COUNTRIES } from "../../helpers/creditBonus/helper";

const DEFAULT_FORM = {
  site: "",
  promo_type: "",
  promo_name: "",
  promo_comment: "",
  credit_type: "",
  status: 0,
  restricted_countries: "",
  allowed_countries: "",
  start_date: "",
  end_date: "",
  credit_end_date: "",
  promo_min_deposit: "",
  promo_max_credit: "",
  // promo_timezone: "",
  promo_timezone_gmt: "",
  agent_based: "",
};

const REQUIRED = [
  "site",
  "promo_type",
  "promo_name",
  "start_date",
  "end_date",
  "promo_min_deposit",
  "promo_max_credit",
  "credit_type",
  // "promo_timezone",
  "promo_timezone_gmt",
  "credit_end_date",
];

const CampaignForm = ({ modalRef, handleApi }) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [disableBtn, setDisableBtn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { getResponse, setResponse } = useContext(ResponseContext);
  const formRef = useRef(null);
  const response = getResponse("create_campaign");

  const handleClose = () => modalRef.current();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    CreateCampaign({
      form,
      email: localStorage.getItem("userEmail"),
      setIsLoading,
      setResponse,
      formRef,
      onSuccess: () => {
        // setForm(DEFAULT_FORM);
        handleApi();
        handleClose();
      },
    });
  };

  useEffect(() => {
    setDisableBtn(REQUIRED.some((k) => !form[k]));
  }, [form]);

  const countryOptions = COUNTRIES.map((c) => ({ value: c, label: c }));

  function toSelectValue(str) {
    return str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => ({ value: s, label: s }));
  }

  function fromSelectValue(selected) {
    return selected.map((s) => s.value).join(", ");
  }

  return (
    <div className="st-campaign-form w-full max-h-[80vh] overflow-y-auto">
      <div className="flex h-full flex-col justify-center gap-4 p-6 pl-2">
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="flex flex-col gap-6"
        >
          {/* ── Basic info ── */}
          <div className="flex flex-col gap-4">
            <div className="border-b border-gray-100 dark:border-gray-700 pb-2 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                Basic information
              </p>

              <p className="text-xs text-orange-500 flex items-start gap-1">
                <span className="mt-[1px]">⚠</span>
                <span>Note: Only dates and Agent Based can be edited</span>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="site" value="Site *" />
                <Select
                  id="site"
                  name="site"
                  value={form.site}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    Select site
                  </option>
                  <option value="GOAU">GOAU</option>
                  <option value="GOMU">GOMU</option>
                  <option value="GOCY">GOCY</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="promo_type" value="Promo Type *" />
                <TextInput
                  id="promo_type"
                  name="promo_type"
                  placeholder="Promo type code"
                  value={form.promo_type}
                  onChange={handleChange}
                  pattern="[a-zA-Z0-9_ ]*"
                  helperText={
                    <span className="text-xs text-gray-500 mt-1">
                      Letters and numbers only, no spaces or special characters.
                    </span>
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="promo_name" value="Promo Name *" />
                <TextInput
                  id="promo_name"
                  name="promo_name"
                  placeholder="50% Credit Bonus"
                  value={form.promo_name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="credit_type" value="Credit Type *" />
                <TextInput
                  id="credit_type"
                  name="credit_type"
                  placeholder=""
                  value={form.credit_type}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="promo_comment" value="Promo Comment" />
                <Textarea
                  id="promo_comment"
                  name="promo_comment"
                  placeholder="Short description of the promotion"
                  value={form.promo_comment}
                  onChange={handleChange}
                  rows={1}
                />
              </div>
              <div>
                <Label htmlFor="agent_based" value="Agent Based" />
                <TextInput
                  id="agent_based"
                  name="agent_based"
                  placeholder="Agent-based identifier"
                  value={form.agent_based}
                  onChange={handleChange}
                  helperText={
                    <span className="text-xs text-gray-500 mt-1">
                      Separate multiple agent IDs with commas — e.g.{" "}
                      <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-gray-600">
                        1234, 5678, 9012
                      </code>
                    </span>
                  }
                />
              </div>
              {/*  <div className="flex flex-col justify-end pb-1">
                <Label value="Status" />
                <div className="mt-2">
                  <ToggleSwitch
                    checked={form.status}
                    label={form.status ? "Active" : "Inactive"}
                    onChange={(checked) =>
                      setForm((prev) => ({ ...prev, status: checked }))
                    }
                  />
                </div>
              </div> */}
            </div>
          </div>
          {/* ── Dates and amounts ── */}
          <div className="flex flex-col gap-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 pb-1 border-b border-gray-100 dark:border-gray-700">
              Dates and amounts
            </p>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="start_date" value="Start Date *" />
                <TextInput
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div>
                <Label htmlFor="end_date" value="End Date *" />
                <TextInput
                  id="end_date"
                  name="end_date"
                  type="date"
                  value={form.end_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div>
                <Label htmlFor="credit_end_date" value="Credit End Date *" />
                <TextInput
                  id="credit_end_date"
                  name="credit_end_date"
                  type="date"
                  value={form.credit_end_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="promo_min_deposit" value="Min Deposit *" />
                <TextInput
                  id="promo_min_deposit"
                  name="promo_min_deposit"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.promo_min_deposit}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="promo_max_credit" value="Max Credit *" />
                <TextInput
                  id="promo_max_credit"
                  name="promo_max_credit"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.promo_max_credit}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          {/* ── Countries and timezone ── */}
          <div className="flex flex-col gap-4">
            <div className="border-b border-gray-100 dark:border-gray-700 pb-2 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                Countries and Timezone
              </p>

              <p className="text-xs text-orange-500 flex items-start gap-1">
                <span className="mt-[1px]">⚠</span>
                <span>
                  Allowed Countries and Restricted Countries cannot be used at
                  the same time
                </span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="allowed_countries" value="Allowed Countries" />
                <ReactSelect
                  inputId="allowed_countries"
                  isMulti
                  options={countryOptions}
                  placeholder="e.g. Australia, New Zealand, China"
                  isDisabled={form.restricted_countries.trim() !== ""}
                  value={toSelectValue(form.allowed_countries)}
                  onChange={(selected) =>
                    handleChange({
                      target: {
                        name: "allowed_countries",
                        value: fromSelectValue(selected),
                      },
                    })
                  }
                />
              </div>
              <div>
                <Label
                  htmlFor="restricted_countries"
                  value="Restricted Countries"
                />
                <ReactSelect
                  inputId="restricted_countries"
                  isMulti
                  options={countryOptions}
                  placeholder="e.g. United States, Iran, North Korea"
                  isDisabled={form.allowed_countries.trim() !== ""}
                  value={toSelectValue(form.restricted_countries)}
                  onChange={(selected) =>
                    handleChange({
                      target: {
                        name: "restricted_countries",
                        value: fromSelectValue(selected),
                      },
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/*    <div>
                <Label htmlFor="promo_timezone" value="Timezone *" />
                <TextInput
                  id="promo_timezone"
                  name="promo_timezone"
                  placeholder="e.g. Australia/Sydney"
                  value={form.promo_timezone}
                  onChange={handleChange}
                />
              </div> */}
              <div>
                <Label htmlFor="promo_timezone_gmt" value="Timezone GMT *" />

                <TextInput
                  id="promo_timezone_gmt"
                  name="promo_timezone_gmt"
                  placeholder="e.g. GMT+10"
                  value={form.promo_timezone_gmt}
                  onChange={handleChange}
                  pattern={String.raw`^GMT([+\-](1[0-4]|0?\d))$`}
                  title="Format must be like GMT+10, GMT-5, GMT+0"
                />
              </div>
            </div>
            <div>
              {response?.status === false && (
                <Badge color="failure">{response?.message}</Badge>
              )}
            </div>
          </div>
          {/* ── Actions ── */}
          <div className="flex mt-2 items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
            <button
              type="button"
              className="text-sm font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              onClick={handleClose}
            >
              Cancel
            </button>
            <Button type="submit" disabled={disableBtn}>
              {isLoading ? "Creating..." : "Create campaign"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignForm;
